import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  ViewChild,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputGroup } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputIconModule } from 'primeng/inputicon';
import { InputMaskModule } from 'primeng/inputmask';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MenuModule } from 'primeng/menu';
import { Action } from '../../../models/core/action';
import { MenuComponent } from '../panels/menu.component';
import { BaseControlComponent } from './base-control.component';
import { FormFieldComponent } from './form-field.component';

@Component({
  selector: 'app-claim-number',
  imports: [
    ReactiveFormsModule,
    CommonModule,
    InputTextModule,
    FormFieldComponent,
    IconFieldModule,
    InputIconModule,
    InputNumberModule,
    InputMaskModule,
    MenuModule,
    InputGroupAddonModule,
    InputGroup,
    ButtonModule,
    MenuComponent,
  ],

  template: `
    <app-form-field
      [control]="control"
      [id]="id"
      [label]="setLabel()"
      [helpText]="helpText"
      [showHelpText]="showHelpText"
      [tooltip]="tooltip"
    >
      <div class="tw-flex tw-items-center">
        <p-inputgroup>
          <input
            #inputElement
            pInputText
            autocomplete="off"
            [formControl]="control"
            [id]="id"
            [attr.maxlength]="maxlength"
            class="app-input-border"
            [style]="{
              width: '100%',
              height: '40px',
              'border-radius': '6px 0 0 6px',
            }"
            [pSize]="large ? 'large' : null"
            [class]="{
              'ng-invalid': touchedInvalid,
              'ng-dirty': touchedInvalid,
            }"
            (paste)="onPaste($event, inputElement)"
          />
          <p-inputgroup-addon
            styleClass="app-input-border"
            [style]="{
              height: '40px',
              padding: '0',
            }"
          >
            <button
              type="button"
              tabindex="0"
              class="p-button p-button-icon-only p-button-secondary p-button-text menu-button focus-ring-icon-button"
              style="width: 35px; height: 38px; min-width: 35px;"
              aria-label="Claim number search options"
              [attr.aria-expanded]="isMenuExpanded"
              aria-haspopup="menu"
              (click)="menu.toggle($event)"
              (keydown.enter)="menu.toggle($event)"
              (keydown.space)="menu.toggle($event)"
            >
              <span
                class="p-button-icon fa-ellipsis-vertical fa-light"
                aria-hidden="true"
              ></span>
            </button>
          </p-inputgroup-addon>
        </p-inputgroup>
        <app-menu
          [items]="actions"
          #menu
          appendTo="body"
          (click)="setSearchType($event)"
          (visibilityChange)="isMenuExpanded = $event"
        />
      </div>
    </app-form-field>
  `,
  styles: `
    :host ::ng-deep p-floatlabel:has(p-inputgroup) label {
      max-width: calc(100% - 3.75rem);
      display: inline-block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  `,
})
export class ClaimNumberComponent
  extends BaseControlComponent
  implements AfterViewInit
{
  @ViewChild('inputElement', { read: ElementRef })
  inputElement: ElementRef;

  @Input()
  icon: string;

  @Input()
  large = false;

  @Input()
  maxlength: number;

  @Input()
  searchType: SearchType = 'ClaimNumberIs';

  @Input()
  searchLabel: string;

  @Input()
  trimPaste = true;

  /** Tracks menu expanded state for accessibility */
  isMenuExpanded = false;

  actions: Action[] = [
    { label: 'Claim number', key: 'is' },
    { label: 'Claim/Event number begins with', key: 'begin-with' },
  ];

  ngAfterViewInit(): void {
    if (this.placeholder) {
      this.inputElement.nativeElement.setAttribute(
        'placeholder',
        this.placeholder,
      );
    }
  }

  protected setSearchType(action: Action) {
    this.searchType =
      action.key === 'is' ? 'ClaimNumberIs' : 'ClaimNumberBeginsWith';
  }

  public setLabel(): string {
    this.searchLabel =
      this.searchType === 'ClaimNumberIs'
        ? 'Claim number'
        : 'Claim/Event number begins with';
    return this.searchLabel;
  }

  protected onPaste(event: ClipboardEvent, input: HTMLInputElement) {
    if (!this.trimPaste) {
      return;
    }

    event.preventDefault();
    const clipboardData = event.clipboardData;
    if (clipboardData) {
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      const value = input.value;
      const pastedText = clipboardData.getData('text');

      const newString =
        value.substring(0, start) + pastedText.trim() + value.substring(end);

      const newCursorPosition =
        start + event.clipboardData.getData('text').length;
      this.control.setValue(newString);

      requestAnimationFrame(() => {
        this.inputElement.nativeElement.setSelectionRange(
          newCursorPosition,
          newCursorPosition,
        );
      });
    }
  }
}

export type SearchType = 'ClaimNumberIs' | 'ClaimNumberBeginsWith';
